import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { ChevronDownIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type SelectProps<T extends string = string> = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  label?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  options: SelectOption<T>[];
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, required, error, placeholder, options, className, id, value, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-ink-soft">
          {required && (
            <span aria-hidden="true" className="mr-0.5 text-danger">
              *
            </span>
          )}
          {label}
        </label>
      )}
      <div
        className={cn(
          'relative flex items-center rounded-xl border bg-white shadow-sm transition-colors',
          error ? 'border-red-400 focus-within:border-red-500' : 'border-slate-300 focus-within:border-brand-500',
          'focus-within:ring-2',
          error ? 'focus-within:ring-red-200' : 'focus-within:ring-brand-200',
        )}
      >
        <select
          ref={ref}
          id={inputId}
          value={value ?? ''}
          className={cn(
            'min-w-0 flex-1 appearance-none bg-transparent py-3 pl-3 pr-9 text-base text-ink outline-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
            !value && placeholder ? 'text-ink-faint' : '',
            className,
          )}
          aria-invalid={Boolean(error)}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-3 h-4 w-4 text-ink-muted" />
      </div>
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
});
